import api from './api';
import type { PlanTier } from '@/redux/slices/planSlice';
import type { PlanFeature } from '@erpgold/shared';

// Billing payment methods supported by the payment gateway
export type BillingPaymentMethod = 'vnpay' | 'momo' | 'zalopay' | 'bank_transfer';

// ADR-013: API JSON snake_case end-to-end. Mirror BE PlanController.current().
// `usePlanData` hook transform sang shape camelCase Redux internal state.
export interface PlanUsageDto {
  products: number;
  staffs: number;
  stores: number;
  monthly_orders: number;
}

export interface PlanInfo {
  tier: PlanTier;
  expires_at: string | null;
  trial_days_left: number | null;
  grace_days_left: number | null;
  features: PlanFeature[];
  usage: PlanUsageDto;
}

export interface PaymentHistoryItem {
  _id: string;
  invoiceCode: string;
  tier: PlanTier;
  billingCycle: 'monthly' | 'yearly';
  amount: number;
  status: 'paid' | 'pending' | 'failed' | 'refunded';
  paidAt: string | null;
  method: 'cash' | 'transfer' | 'card' | 'momo';
}

export interface UpgradePayload {
  tier: PlanTier;
  billingCycle: 'monthly' | 'yearly';
  couponCode?: string;
}

// ─── Billing transactions (GET /billing/payments) ─────────────────────────
// Decimal128 từ Mongoose (không lean) serialize thành { $numberDecimal: "..." }

export type BillingTransactionStatus = 'pending' | 'success' | 'failed' | 'cancelled';
export type BillingTransactionType =
  | 'subscription_change'
  | 'subscription_renewal'
  | 'proration'
  | 'refund';

export interface BillingTransactionDto {
  _id: string;
  type: BillingTransactionType;
  status: BillingTransactionStatus;
  amount: number | { $numberDecimal: string };
  currency: string;
  description: string;
  failure_reason: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface BillingPaymentsResponse {
  data: BillingTransactionDto[];
  meta: { page: number; limit: number; total: number };
}

// ─── Billing plans list (GET /billing/plans) ──────────────────────────────

export interface PlanPricingDto {
  monthly: number;
  yearly: number;
}

export interface PlanLimitsDto {
  max_stores: number | null;
  max_users: number | null;
  max_products: number | null;
}

export interface PlanListItem {
  plan_id: string;
  name: string;
  display_name: string;
  slug: string;
  tier: number;
  pricing: PlanPricingDto;
  limits: PlanLimitsDto;
  features: PlanFeature[];
  sort_order: number;
}

// ─── Payment methods (GET /billing/payment-methods) ──────────────────────

export interface PaymentMethod {
  payment_method_id: string;
  code: string;
  name: string;
  description: string;
  icon: string;
  is_default: boolean;
}

// ─── QR payment (POST /billing/payment/vnpay/qr) ─────────────────────────

export interface CreatePaymentBody {
  billing_cycle: 'monthly' | 'yearly';
  plan_id: string;
  payment_method: BillingPaymentMethod;
}

export interface PaymentResult {
  qr_code: string;
  payment_id: string;
  amount: number;
  exp_minutes: number;
}

// ─── Payment status (GET /billing/payments/:id/status) ───────────────────

export type PaymentStatusValue = 'pending' | 'success' | 'failed' | 'cancelled';

export interface PaymentStatusDto {
  payment_id: string;
  status: PaymentStatusValue;
  amount: number;
  provider: string;
  created_at: string;
}

// ─── Subscription (GET /billing/subscription) ────────────────────────────

export interface SubscriptionPlanPopulated {
  plan_id: string;
  slug: string;
  display_name: string;
  tier: number;
  pricing: PlanPricingDto;
  features: PlanFeature[];
  limits: PlanLimitsDto;
}

export interface SubscriptionDowngradePlanPopulated {
  plan_id: string;
  slug: string;
  display_name: string;
  tier: number;
}

export interface SubscriptionDto {
  tenant_id: string;
  plan_id: SubscriptionPlanPopulated | string;
  plan_name: string;
  status: 'trial' | 'active' | 'grace' | 'suspended' | 'cancelled';
  billing_cycle: 'monthly' | 'yearly';
  current_period_start: string;
  current_period_end: string;
  trial_ends_at: string | null;
  grace_ends_at: string | null;
  downgrade_scheduled_plan_id: SubscriptionDowngradePlanPopulated | string | null;
}

// ─── Change plan preview (GET /billing/subscription/change-plan/preview) ─

export interface ChangePlanPreviewDto {
  valid: boolean;
  is_upgrade: boolean;
  is_downgrade: boolean;
  proration_amount: number;
  effective_immediately: boolean;
  effective_date: string;
  new_plan_display_name: string;
  current_plan_display_name: string;
  message: string;
  error?: string;
}

// ─── Change plan result (POST /billing/subscription/change-plan) ──────────

export interface ChangePlanResultDto {
  applied_immediately: boolean;
  message: string;
}

// ─── Service ──────────────────────────────────────────────────────────────

export const planService = {
  current: () => api.get<{ data: PlanInfo }>('/plan').then((r) => r.data.data),

  getPlans: () =>
    api
      .get<{ data: PlanListItem[] }>('/billing/plans')
      .then((r) => r.data.data),

  getPaymentMethods: () =>
    api
      .get<{ data: PaymentMethod[] }>('/billing/payment/methods')
      .then((r) => r.data.data),

  paymentCreate: (body: CreatePaymentBody) =>
    api
      .post<{ data: PaymentResult }>('/billing/payment/create', body)
      .then((r) => r.data.data),

  getPaymentStatus: (paymentId: string) =>
    api
      .get<{ data: PaymentStatusDto }>(`/billing/payment/status?trans_id=${paymentId}`)
      .then((r) => r.data.data),

cancelPayment: (paymentId: string) =>
    api
      .post<{ success: boolean; message: string }>(`/billing/payment/cancel/${paymentId}`)
      .then((r) => r.data),

  upgrade: (body: UpgradePayload) =>
    api.post<{ checkoutUrl: string }>('/plan/upgrade', body).then((r) => r.data),

  cancel: (reason?: string) =>
    api.post<{ ok: true }>('/plan/cancel', { reason }).then((r) => r.data),

  paymentHistory: () =>
    api.get<PaymentHistoryItem[]>('/plan/payments').then((r) => r.data),

  getSubscription: () =>
    api.get<{ data: SubscriptionDto }>('/billing/subscription').then((r) => r.data.data),

  previewChangePlan: (newPlanSlug: string) =>
    api
      .get<{ data: ChangePlanPreviewDto }>(
        `/billing/subscription/change-plan/preview?new_plan_slug=${encodeURIComponent(newPlanSlug)}`,
      )
      .then((r) => r.data.data),

  changePlan: (newPlanSlug: string) =>
    api
      .post<{ data: ChangePlanResultDto }>('/billing/subscription/change-plan', {
        new_plan_slug: newPlanSlug,
      })
      .then((r) => r.data.data),

  getBillingPayments: (page = 1, limit = 20) =>
    api
      .get<{ success: boolean; data: BillingPaymentsResponse }>('/billing/payments', {
        params: { page, limit },
      })
      .then((r) => r.data.data),

  cancelSubscription: () =>
    api
      .patch<{ data: { message: string }; success: boolean }>('/billing/subscription/cancel')
      .then((r) => r.data),
};
